<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :lockable="lockable"
            :has-lock="hasLock"
            :document-lock="documentLock"
            @force-takeover="attemptTakeover"
            @takeover="takeOver"

            :show-preview-button="shouldShowPreviewButton()"
            @click-preview="submitPreviewSite(form.language.iso, form.uri)"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :seo-data="data ? data.latest_seo : null"

            model="App\Models\SlotReview"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">* Slot</p>
            <reference
                create-text="Create new slot and link slot to this page"
                link-text="Link an existing slot"
                :language="form.language"
                v-model="form.slot"
                :should-open-new-window=true
                :creatable="false"
                model-data="Slot"
                :query="{
                    no_slot_review: true,
                    variant_of: baseDocumentSlotId,
                }"
                :order="{
                    'name': 'asc'
                }"
                :error="errors.slot_id ? errors.slot_id[0] : null"
            />
        </div>

        <base-page-fields
            class="mt-3"
            :form="form"
            :errors="errors"
            :excluded-types="['Slot Review']"
            :editable-timestamps="true"
            :publishable="publishable"
        ></base-page-fields>

        <div class="flex mt-2">
            <div
                class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <p class="text-gray-700 mb-4">Main Casino</p>
                <reference
                    create-text="Create new casino and link casino to this page"
                    link-text="Link an existing casino"
                    v-model="form.casino"
                    :language="form.language"
                    :creatable="false"
                    :should-open-new-window=true
                    model-data="Casino"
                    :query="{
                        'casinoReview.status_id': 1
                    }"
                    :order="{
                        'casinos.name': 'asc'
                    }"
                    :error="errors.casino_id ? errors.casino_id[0] : null"
                />
            </div>
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Main Bonuses</p>
            <multi-reference
                create-text="Create new bonus and link bonus to this page"
                link-text="Link an existing bonus"
                :creatable="false"
                v-model="form.bonuses"
                model-data="Bonus"
                :language="form.language"
                :should-open-new-window=true
                :query="{
                    casino_id: form.casino_id
                }"
                :pre-filled="{
                    casino: form.casino || null,
                }"
                :order="{
                    'name': 'asc'
                }"
                :error="errors.bonuses ? errors.bonuses[0] : null"
            />
        </div>

        <input-text
            name="review_score"
            label="Review Score"
            v-model="form.review_score"
            class="mt-2"
            :error="errors.review_score ? errors.review_score[0] : null"
        />

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Author</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                v-model="form.author"
                :creatable="false"
                model-data="Author"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :language="form.language"
                :error="errors.author_id ? errors.author_id[0] : null"
                :excluded-ids="excludedAuthors"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Fact checked by</p>
            <reference
                create-text="Create new author and link author to this page"
                link-text="Link an existing author profile"
                two-step-create
                :creatable="false"
                :should-open-new-window=true
                :language="form.language"
                v-model="form.factChecker"
                model-data="Author"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'authors.name': 'asc'
                }"
                :excluded-ids="excludedFactCheckers"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Published For</p>
            <reference
                create-text="Create new country and link country to this page"
                link-text="Link an existing country"
                two-step-create
                v-model="form.publishedFor"
                model-data="Country"
                :order="{
                    'name': 'asc'
                }"
            />
        </div>


        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Where to Play</p>
            <multi-reference
                create-text="Create new casino review and link casino review to this page"
                link-text="Link existing casino reviews"
                :creatable="false"
                v-model="form.casinoReviews"
                model-data="Casino Review"
                :should-open-new-window=true
                :query="{
                    status_id: 1
                }"
                :order="{
                    'casino_reviews.name': 'asc'
                }"
                :language="form.language"
            />
        </div>

         <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Similar Slots</p>
            <multi-reference
                create-text="Create new similar slot and link similar slot to this page"
                link-text="Link existing similar slots"
                :creatable="false"
                :language="form.language"
                :should-open-new-window=true
                :excluded-ids="excludedSlots"
                v-model="form.similarSlotReviews"
                :query="{
                    status_id: 1
                }"
                :order="{
                    'slot_reviews.name': 'asc'
                }"
                model-data="Slot Review"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full px-4 pb-3 shadow-sm max-w-4xl mt-3"
        >
            <input-rich-text
                name="overview"
                label="Intro Text (below H1)"
                v-model="form.overview"
                :error="errors.overview ? errors.overview[0] : null"
                :maxlength=6000
                :language="form.language"
            />
            <p class="my-1 text-xs text-gray-600">You can use these available shortcodes in Intro Text (below H1) field: [current_month], [month], [current_year], [year] and [current_abrev_month]</p>

            <Select
                label="Add Trust Badge"
                name="overview_trust_badge"
                class="mt-3"
                v-model="form.overview_trust_badge"
                :options="[
                        { value: '', name: 'No Badge'},
                        { value: 'verified-experts', name: 'Verified experts for'},
                        { value: 'safety-checked', name: 'Safety checked by'},
                        { value: 'approved', name: 'Approved by'},
                        { value: 'tested', name: 'Tested by'},
                    ]"
                :error="errors.overview_trust_badge ? errors.overview_trust_badge[0] : null"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full px-4 pb-3 shadow-sm max-w-4xl mt-3"
        >

            <media-reference
                v-model="form.image"
                :language="form.language"
                class="mt-3"
                label="Image"
                name="image_id"
                :media-types="['Image', 'Screenshot']"
            />

            <Select
                label="Add Trust Badge"
                name="overview_trust_badge"
                class="mt-3"
                v-model="form.image_trust_badge"
                :options="[
                        { value: '', name: 'No Badge'},
                        { value: 'verified-experts', name: 'Verified experts for'},
                        { value: 'safety-checked', name: 'Safety checked by'},
                        { value: 'approved', name: 'Approved by'},
                        { value: 'tested', name: 'Tested by'},
                    ]"
                :error="errors.image_trust_badge ? errors.image_trust_badge[0] : null"
            />


            <div
                class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <p class="text-gray-700 mb-4">Affiliate link for screenshot</p>

                <rocket-link
                    :language="form.language"
                    v-model="form.image_link"
                    class="mt-5"
                    label=""
                    :link-types="[4]"
                    :hide-link-text="true"
                    :resettable="true"
                    :error="errors.image_link ? errors.image_link[0] : null"
                />
            </div>
        </div>

        <div class="mt-3">
            <span class="text-gray-700">Content Blocks</span>
            <body-content
                :gallery="form.gallery"
                :language="form.language"
                v-model="form.contentBlocks"
                :errors="errors"
                :content-section-with-how-to="contentSectionWithHowTo"
                :content-section-with-bonus-list-top="contentSectionWithBonusListTop"
                :content-section-with-bonus-list-bottom="contentSectionWithBonusListBottom"
                :content-sections-with-ranking="contentSectionsWithRanking"
                :document-class="className"
                :document-id="data ? data.id : null"
                @add-content-block="addContentBlock"
                @add-content-section="addContentSection"
                @delete-content-block="deleteContentBlock"
                @delete-content-section="deleteContentSection"
                @load-content-blocks="loadContentBlocks"
                @add-how-to="howToAdded"
                @delete-how-to="howToDeleted"
                @add-bonus-list-top="bonusListTopAdded"
                @delete-bonus-list-top="bonusListTopDeleted"
                @add-bonus-list-bottom="bonusListBottomAdded"
                @delete-bonus-list-bottom="bonusListBottomDeleted"
                @add-ranking="rankingAdded"
                @delete-ranking="rankingDeleted"
            ></body-content>
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Gallery</p>
            <multi-reference
                create-text="Create new gallery and link gallery to this page"
                link-text="Link existing galleries"
                :language="form.language"
                :creatable="false"
                :linkable="false"
                :deletable="true"
                v-model="form.gallery"
                model-data="Media"
            />
        </div>

        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <p class="text-gray-700 mb-4">Sticky CTA</p>
            <reference
                create-text="Create new Sticky CTA and link Sticky CTA to this page"
                link-text="Link existing Sticky CTA"
                v-model="form.stickyCTA"
                :should-open-new-window="true"
                model-data="Sticky-C-T-A"
                :language="form.language"
                :editable="true"
            />
        </div>

        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import pageFormData from "../mixins/pageFormData";
import contentBlocksForm from "../mixins/contentBlocksForm";

export default {
    name: "SlotReviewForm",
    mixins: [crudForm, pageFormData, contentBlocksForm],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                h1_anchor: this.getFormData("h1_anchor"),
                review_score: this.getFormData("review_score"),
                overview: this.getFormData("overview"),
                overview_trust_badge: this.getFormData("overview_trust_badge"),
                slot_id: this.getFormData("slot_id"),
                slot: this.getFormData("slot"),
                casino_id: this.getFormData("casino_id"),
                casino: this.getFormData("casino"),
                bonuses: this.getReferenceData("bonuses"),
                author_id: this.getFormData("author_id"),
                author: this.getFormData("author"),
                fact_checker_id: this.getFormData("fact_checker_id"),
                factChecker: this.getFormData("fact_checker"),
                published_for_id: this.getFormData("published_for_id", this.languageData.default_country_id),
                publishedFor: this.getFormData("published_for", this.languageData.default_country),
                gallery: this.getFormData("gallery", []),
                similarSlotReviews: this.getFormData("similar_slot_reviews"),
                casinoReviews: this.getFormData("casino_reviews"),
                image_id: this.getFormData("image_id"),
                image: this.getFormData("image"),
                image_trust_badge: this.getFormData("image_trust_badge"),
                image_link: this.getFormData("image_link"),
                stickyCTA: this.getFormData("sticky_c_t_a"),
                sticky_cta_id: this.getFormData("sticky_cta_id"),
                created_at: this.getFormData("created_at") || new Date(),
                created_at_override: this.getFormData("created_at_override"),
                updated_at: this.getFormData("updated_at") || new Date(),
                updated_at_override: this.getFormData("updated_at_override"),
                variants: this.getFormData('variants', []),
            },
        };
    },
    created() {
        if (!this.data?.id) {
            this.form.overview_trust_badge = 'approved';
            this.form.image_trust_badge = 'tested';
        }
    },
    computed: {
        excludedAuthors () {
            return [this.form.fact_checker_id];
        },
        excludedFactCheckers () {
            return [this.form.author_id];
        },
        excludedSlots () {
            return [this.form.slot_id];
        },
        baseDocumentSlotId() {
            return this.form?.baseDocument?.slot_id;
        }
    },
    watch: {
        "form.slot": function (newValue) {
            this.form.slot_id = newValue ? newValue.id : null;
        },
        "form.image": function (newValue) {
            this.form.image_id = newValue ? newValue.id : null;
        },
        "form.author": function (newValue) {
            this.form.author_id = newValue ? newValue.id : null;
        },
        "form.factChecker": function (newValue) {
            this.form.fact_checker_id = newValue ? newValue.id : null;
        },
        "form.publishedFor": function (newValue) {
             this.form.published_for_id = newValue ? newValue.id : null;
        },
        "form.casino": function (newValue) {
            this.form.casino_id = newValue ? newValue.id : null;
        },
        "form.stickyCTA": function (newValue) {
            this.form.sticky_cta_id = newValue ? newValue.id : null;
        }
    }
};
</script>
